/**
 * Parses "Analiz Değerleri" block from product.description HTML
 * and renders rows into product-nutrition-panel tables.
 */
(function () {
  const PANEL_SELECTOR = '[data-hp-nutrition-analiz]';
  const DESC_SELECTOR = 'script[type="application/json"][data-hp-product-description]';
  /** Birincil başlık bulunamazsa sırayla denenir. */
  const SECTION_HEADING_FALLBACKS = [
    'Analitik Bileşenler',
    'Analiz Raporu',
    'Besin Değerleri',
  ];

  /** Analiz satırı / blok uzunluk eşikleri (açıklama paragrafları genelde daha uzun). */
  const MAX_ANALIZ_LINE_LENGTH = 88;
  const MAX_ANALIZ_LABEL_LENGTH = 52;
  /** Son analiz değerinden sonra kalan metin bu uzunlukta veya fazlaysa kesilir. */
  const MIN_TAIL_AFTER_LAST_VALUE = 12;
  /** Tek satırda izin verilen üst sınır (uzun enerji satırı gibi istisnalar). */
  const MAX_ANALIZ_LINE_LENGTH_WITH_VALUE = 118;
  /** Analitik tablo satır üst sınırı (vitamin listesi vb. kesilir). */
  const MAX_ANALIZ_ROWS = 20;

  const ANALIZ_SUBHEADING_START =
    /(?:^|\s)(Besin\s*Katk[ıi]\s*Maddeleri|Besin\s*katk[ıi]|Vitaminler?|Katk[ıi]\s*maddeleri|Her\s+\d|1\s*kg\s*mama|İçindekiler|Mineral\s*maddeler|Spesifik\s*güvenilirlik)/iu;

  let plainInlineValueRe = null;

  function getPlainInlineValueRe() {
    if (!plainInlineValueRe) {
      const n = '(?:\\d{1,3}(?:\\.\\d{3})+(?:,\\d+)?|\\d+(?:[.,]\\d+)?)';
      plainInlineValueRe = new RegExp(
        `%[\\d,\\.]+|${n}\\s*kcal/100\\s*g\\s*\\([^)]+\\)|${n}\\s*mg/kg|${n}\\s*IU/kg|${n}\\s*g/kg|${n}\\s*mg\\b|${n}\\s*IU\\b|${n}\\s*kcal/kg|${n}\\s*MJ/kg`,
        'gi'
      );
    }
    return plainInlineValueRe;
  }

  function normalizeHeading(text) {
    return (text || '')
      .toLocaleLowerCase('tr-TR')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function normalizeText(text) {
    return (text || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
  }

  /** Açıklamadaki "Nem —" / "Protein:" gibi ayraçları etiketten düşürür. */
  function cleanLabel(text) {
    return normalizeText(text)
      .replace(/\s*[–—\-−]\s*$/u, '')
      .replace(/^\s*[–—\-−]\s*/u, '')
      .replace(/\s*[:：]+\s*$/u, '')
      .replace(/^\s*[:：]+\s*/u, '')
      .trim();
  }

  function isAnalizSubheadingLine(text) {
    const t = normalizeText(text);
    if (!t) return false;
    if (ANALIZ_SUBHEADING_START.test(t)) {
      const afterColon = t.split(/[:：]/).pop() || '';
      if (looksLikeNutritionValue(afterColon.trim())) return false;
      return true;
    }
    return false;
  }

  function trimLabelAtSubheading(label) {
    let t = normalizeText(label);
    const idx = t.search(ANALIZ_SUBHEADING_START);
    if (idx > 0) t = t.slice(0, idx).trim();
    if (idx === 0) return '';
    return cleanLabel(t);
  }

  function truncateAtAnalizSubheading(text) {
    const t = normalizeText(text);
    const idx = t.search(ANALIZ_SUBHEADING_START);
    if (idx > 0) return t.slice(0, idx).trim();
    return t;
  }

  function isStructuredSectionNode(node) {
    if (!node || node.nodeType !== 1) return false;
    const tag = node.tagName;
    if (/^(UL|OL|TABLE|DL|TBODY|THEAD|TFOOT)$/i.test(tag)) return true;
    if (tag === 'DIV' && node.querySelector(':scope > ul, :scope > ol, :scope > table')) {
      return true;
    }
    return false;
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function isBlockHeading(el) {
    if (!el || el.nodeType !== 1) return false;
    const tag = el.tagName;
    if (/^H[1-6]$/.test(tag)) return true;
    if (tag === 'P' || tag === 'DIV') {
      const t = normalizeText(el.textContent);
      if (t.length > 0 && t.length < 80 && !el.querySelector('table')) return true;
    }
    return false;
  }

  function headingMatches(el, targetNorm) {
    const own = normalizeHeading(el.textContent);
    if (own === targetNorm || own.includes(targetNorm)) return true;
    if (el.tagName === 'P' || el.tagName === 'DIV' || el.tagName === 'TD') {
      const strong = el.querySelector('strong, b');
      if (strong && normalizeHeading(strong.textContent).includes(targetNorm)) return true;
    }
    return false;
  }

  function findHeadingElement(doc, targetNorm) {
    const candidates = doc.body.querySelectorAll(
      'h1, h2, h3, h4, h5, h6, p, strong, b, span, td, th, div'
    );
    let best = null;
    let bestScore = Infinity;

    for (const el of candidates) {
      if (el.closest('table')) continue;
      if (!headingMatches(el, targetNorm)) continue;

      const len = normalizeText(el.textContent).length;
      let score = len;
      if (/^H[1-6]$/.test(el.tagName)) score -= 40;
      if (len > 280) score += 2000;
      if (score < bestScore) {
        bestScore = score;
        best = el;
      }
    }
    return best;
  }

  function collectSectionNodes(headingEl) {
    const nodes = [];
    const targetNorm = normalizeHeading(headingEl.textContent);
    const body = headingEl.ownerDocument.body;

    function shouldStopNode(node) {
      if (!node || node === headingEl) return false;
      if (isStructuredSectionNode(node)) return false;

      const t = normalizeText(node.textContent);
      if (isAnalizSubheadingLine(t) && !node.querySelector('li, td, tr')) return true;

      if (isBlockHeading(node)) {
        const ht = normalizeHeading(t);
        if (ht && ht !== targetNorm && !ht.includes(targetNorm)) return true;
      }

      const tag = node.tagName;
      if (/^(P|SPAN)$/i.test(tag) && !node.querySelector('ul, ol, table, li, tr')) {
        if (isProseBlock(t)) return true;
      }
      if (tag === 'DIV' && !node.querySelector('ul, ol, table, li, tr') && isProseBlock(t)) {
        return true;
      }
      return false;
    }

    function pushUntilBreak(startEl) {
      let node = startEl;
      while (node) {
        if (shouldStopNode(node)) break;
        nodes.push(node);
        node = node.nextElementSibling;
      }
    }

    if (headingEl.parentElement && headingEl.parentElement !== body) {
      const parent = headingEl.parentElement;
      const parentText = normalizeHeading(parent.textContent);
      if (parentText === targetNorm || parentText.includes(targetNorm)) {
        pushUntilBreak(parent.nextElementSibling);
        if (nodes.length) return nodes;
      }
    }

    pushUntilBreak(headingEl.nextElementSibling);

    if (!nodes.length) {
      let sib = headingEl.parentElement?.nextElementSibling;
      while (sib) {
        if (shouldStopNode(sib)) break;
        nodes.push(sib);
        sib = sib.nextElementSibling;
      }
    }

    return nodes;
  }

  function escapeRegExp(str) {
    return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function extractTextAfterHeading(fullText, headingText) {
    const text = normalizeText(fullText);
    if (!text || !headingText) return null;

    const re = new RegExp(`(?:^|\\s)${escapeRegExp(headingText.trim())}\\s*`, 'iu');
    const match = text.match(re);
    if (!match || match.index == null) return null;

    return text.slice(match.index + match[0].length).trim();
  }

  function endsWithNutritionValue(text) {
    const t = normalizeText(text);
    return (
      /\s+%[\d,\.]+\s*$/i.test(t) ||
      /\s+\d[\d.,\s]*(?:mg\/kg|IU\/kg|kcal\/100\s*g(?:\s*\([^)]+\))?|kcal\/kg|MJ\/kg|\s*IU|\s*mg)\s*$/i.test(t)
    );
  }

  function isProseBlock(text) {
    const t = normalizeText(text);
    if (!t) return false;
    if (t.length <= MAX_ANALIZ_LINE_LENGTH) return false;
    if (t.length <= MAX_ANALIZ_LINE_LENGTH_WITH_VALUE && endsWithNutritionValue(t)) {
      return false;
    }
    return true;
  }

  function truncateAfterLastNutritionValue(text) {
    const t = truncateAtAnalizSubheading(normalizeText(text));
    const valueRe = getPlainInlineValueRe();
    let lastEnd = 0;
    let match;
    valueRe.lastIndex = 0;
    while ((match = valueRe.exec(t)) !== null) {
      lastEnd = match.index + match[0].length;
    }
    if (lastEnd === 0) return t;
    const tail = t.slice(lastEnd).trim();
    if (tail.length >= MIN_TAIL_AFTER_LAST_VALUE) {
      return t.slice(0, lastEnd).trim();
    }
    return t;
  }

  function truncateBeforeFeedingGuide(text) {
    return truncateAfterLastNutritionValue(text);
  }

  function looksLikeNutritionValue(value) {
    if (value == null || value === '') return false;
    const v = normalizeText(value);
    return (
      /^%[\d,\.]+$/i.test(v) ||
      (/\d/.test(v) && /(?:mg\/kg|IU\/kg|kcal|MJ\/kg|\bIU\b|\bmg\b|%)/i.test(v))
    );
  }

  function parseSemicolonSeparatedRows(text) {
    if (!text.includes(';')) return [];
    const rows = [];
    for (const part of text.split(';')) {
      const line = normalizeText(part);
      if (!line || isAnalizSubheadingLine(line)) break;
      const parsed = splitLabelValue(line);
      if (parsed && parsed.label && parsed.value && looksLikeNutritionValue(parsed.value)) {
        rows.push(parsed);
      }
    }
    return rows;
  }

  function finalizeRows(rows) {
    const out = [];
    for (const row of rows) {
      if (isAnalizSubheadingLine(row.label || '')) break;

      let label = trimLabelAtSubheading(row.label || '');
      label = cleanLabel(label);
      if (!label || label.length < 2 || label.length > MAX_ANALIZ_LABEL_LENGTH) continue;

      const value = row.value != null && row.value !== '' ? normalizeText(row.value) : null;
      const fullLine = normalizeText(`${label} ${value || ''}`);
      if (!value && isProseBlock(fullLine)) continue;
      if (value && !looksLikeNutritionValue(value)) continue;
      if (!value && !/^%[\d,\.]/i.test(label)) continue;

      out.push({ ...row, label, value, strong: !!row.strong, indent: !!row.indent });
      if (out.length >= MAX_ANALIZ_ROWS) break;
    }
    return out;
  }

  function parsePlainInlineRows(text) {
    let slice = truncateBeforeFeedingGuide(text);
    slice = slice.replace(
      /^(Besin Değerleri|Analiz Değerleri|Analitik Bileşenler|Analiz Raporu)\s+/iu,
      ''
    );
    if (!slice) return [];

    if (slice.includes(';') && /%|[\d,\.]+\s*mg\b/i.test(slice)) {
      const semi = parseSemicolonSeparatedRows(slice);
      if (semi.length) return semi;
    }

    const matches = [];
    const valueRe = getPlainInlineValueRe();
    valueRe.lastIndex = 0;
    let match;
    while ((match = valueRe.exec(slice)) !== null) {
      matches.push({
        value: normalizeText(match[0]),
        index: match.index,
        end: match.index + match[0].length,
      });
    }

    if (!matches.length) return [];

    const rows = [];
    for (let i = 0; i < matches.length; i += 1) {
      const current = matches[i];
      const prevEnd = i === 0 ? 0 : matches[i - 1].end;
      let label = cleanLabel(slice.slice(prevEnd, current.index));
      label = trimLabelAtSubheading(label);
      if (!label || label.length < 2 || label.length > MAX_ANALIZ_LABEL_LENGTH) continue;
      rows.push({ label, value: current.value, strong: false, indent: false });
      if (rows.length >= MAX_ANALIZ_ROWS) break;
    }

    return rows;
  }

  function extractSectionFragment(doc, headingText) {
    const targetNorm = normalizeHeading(headingText || 'analiz degerleri');
    const headingEl = findHeadingElement(doc, targetNorm);
    const wrap = doc.createElement('div');

    if (headingEl) {
      const nodes = collectSectionNodes(headingEl);
      if (nodes.length) {
        nodes.forEach((n) => wrap.appendChild(n.cloneNode(true)));
        return wrap;
      }

      const inline = extractTextAfterHeading(headingEl.textContent, headingText);
      if (inline) {
        wrap.textContent = inline;
        return wrap;
      }
    }

    const bodyText = extractTextAfterHeading(doc.body.textContent, headingText);
    if (bodyText) {
      wrap.textContent = bodyText;
      return wrap;
    }

    return null;
  }

  function splitLabelValue(text) {
    const t = normalizeText(text);
    if (!t) return null;

    const colon = t.match(/^(.+?)\s*[:：]\s*(.+)$/);
    if (colon) {
      return {
        label: cleanLabel(colon[1]),
        value: colon[2].trim(),
        strong: false,
        indent: false,
      };
    }

    const tab = t.split(/\t+/);
    if (tab.length >= 2) {
      return {
        label: cleanLabel(tab[0]),
        value: tab.slice(1).join(' ').trim(),
        strong: false,
        indent: false,
      };
    }

    const trailingValue = t.match(
      /^(.+?)\s+((?:%|≥|≤|<|>)?(?:\d{1,3}(?:\.\d{3})+(?:,\d+)?|\d+(?:[.,]\d+)?)(?:\s*(?:mg\/kg|IU\/kg|g\/kg|mg|IU|g|kcal|kj|%))?.*)$/i
    );
    if (trailingValue && trailingValue[1].length >= 2) {
      return {
        label: cleanLabel(trailingValue[1]),
        value: trailingValue[2].trim(),
        strong: false,
        indent: false,
      };
    }

    return { label: cleanLabel(t), value: null, strong: false, indent: false };
  }

  function parseTableRow(tr) {
    const cells = [...tr.querySelectorAll('td, th')];
    if (!cells.length) return null;

    const strong =
      tr.querySelector('th') !== null ||
      cells[0].querySelector('strong, b') !== null ||
      tr.classList.contains('strong');

    if (cells.length === 1) {
      const parsed = splitLabelValue(cells[0].textContent);
      if (!parsed) return null;
      parsed.strong = parsed.strong || strong;
      return parsed;
    }

    if (cells.length === 2) {
      return {
        label: cleanLabel(cells[0].textContent),
        value: normalizeText(cells[1].textContent) || null,
        strong,
        indent: cells[0].classList.contains('indent') || tr.classList.contains('indent'),
      };
    }

    if (cells.length >= 4 && cells.length % 2 === 0) {
      return null;
    }

    return {
      label: cleanLabel(cells[0].textContent),
      value: normalizeText(cells.slice(1).map((c) => c.textContent).join(' ')) || null,
      strong,
      indent: false,
    };
  }

  function parseTable(table) {
    const rows = [];
    table.querySelectorAll('tr').forEach((tr) => {
      const cells = [...tr.querySelectorAll('td, th')];
      if (cells.length >= 4 && cells.length % 2 === 0) {
        for (let i = 0; i < cells.length; i += 2) {
          const label = cleanLabel(cells[i].textContent);
          const value = normalizeText(cells[i + 1].textContent) || null;
          if (label) rows.push({ label, value, strong: false, indent: false });
        }
        return;
      }

      const firstRowLooksLikeHeader =
        tr === table.querySelector('tr') &&
        cells.every((c) => c.tagName === 'TH') &&
        cells.length >= 2;

      if (firstRowLooksLikeHeader) return;

      const row = parseTableRow(tr);
      if (row && row.label) rows.push(row);
    });
    return rows;
  }

  function parseLists(root) {
    const rows = [];
    for (const li of root.querySelectorAll('li')) {
      const lineText = normalizeText(li.textContent);
      if (!lineText) continue;
      if (isAnalizSubheadingLine(lineText)) break;
      const parsed = splitLabelValue(lineText);
      if (parsed && parsed.label) rows.push(parsed);
      if (rows.length >= MAX_ANALIZ_ROWS) break;
    }
    return rows;
  }

  function parseParagraphLines(root) {
    const rows = [];
    root.querySelectorAll('p').forEach((p) => {
      const html = p.innerHTML;
      const parts = html.split(/<br\s*\/?>/gi);
      for (const part of parts) {
        const tmp = document.createElement('div');
        tmp.innerHTML = part;
        const lineText = normalizeText(tmp.textContent);
        if (!lineText) continue;
        if (isAnalizSubheadingLine(lineText)) break;

        if (lineText.includes(';') && /%|[\d,\.]+\s*mg\b/i.test(lineText)) {
          rows.push(...parseSemicolonSeparatedRows(lineText));
          continue;
        }

        if (isProseBlock(lineText)) break;
        const parsed = splitLabelValue(lineText);
        if (parsed && parsed.label) rows.push(parsed);
        if (rows.length >= MAX_ANALIZ_ROWS) break;
      }
    });
    return rows;
  }

  function isMetaRow(row, meta) {
    const labelNorm = normalizeHeading(row.label);
    if (!meta.calories && /^(kalori|enerji)\b/.test(labelNorm)) {
      meta.calories = row;
      return true;
    }
    if (!meta.dailyHeader && /g[uü]nl[uü]k\s*de[gğ]er/.test(labelNorm)) {
      meta.dailyHeader = row.label;
      return true;
    }
    return false;
  }

  function filterMetaFromColumns(columns) {
    const meta = { calories: null, dailyHeader: null };
    const filtered = columns
      .map((col) => col.filter((row) => !isMetaRow(row, meta)))
      .filter((col) => col.length);

    return {
      meta,
      columns: filtered.length ? filtered : [],
    };
  }

  function splitIntoColumns(rows) {
    if (!rows.length) return [];
    if (rows.length <= 3) return [rows];
    const mid = Math.ceil(rows.length / 2);
    return [rows.slice(0, mid), rows.slice(mid)];
  }

  function parseSectionFragment(fragment) {
    const tables = [...fragment.querySelectorAll('table')];

    if (tables.length >= 2) {
      const columns = tables.map((t) => parseTable(t)).filter((c) => c.length);
      if (columns.length) return { columns, footnote: extractFootnote(fragment) };
    }

    if (tables.length === 1) {
      const rows = parseTable(tables[0]);
      if (rows.length) {
        return { columns: splitIntoColumns(rows), footnote: extractFootnote(fragment) };
      }
    }

    let rows = parseLists(fragment);
    if (!rows.length) rows = parseParagraphLines(fragment);

    if (!rows.length) {
      rows = parsePlainInlineRows(fragment.textContent);
    }

    if (!rows.length) {
      const parsed = splitLabelValue(fragment.textContent);
      if (parsed && parsed.label) rows = [parsed];
    }

    if (!rows.length) return null;

    rows = finalizeRows(rows);
    if (!rows.length) return null;

    return { columns: splitIntoColumns(rows), footnote: extractFootnote(fragment) };
  }

  function extractFootnote(fragment) {
    const ps = [...fragment.querySelectorAll('p')];
    for (const p of ps) {
      const t = normalizeText(p.textContent);
      if (t.startsWith('*') || t.toLowerCase().includes('günlük değer')) return t;
    }
    return null;
  }

  function sectionHeadingCandidates(primaryHeading) {
    const primary = primaryHeading || 'Analiz Değerleri';
    const seen = new Set();
    const candidates = [];

    function add(heading) {
      const key = normalizeHeading(heading);
      if (!key || seen.has(key)) return;
      seen.add(key);
      candidates.push(heading);
    }

    add(primary);
    SECTION_HEADING_FALLBACKS.forEach(add);
    return candidates;
  }

  function parseDescription(html, headingText) {
    if (!html || !html.trim()) return null;

    const doc = new DOMParser().parseFromString(html, 'text/html');

    for (const heading of sectionHeadingCandidates(headingText)) {
      const fragment = extractSectionFragment(doc, heading);
      if (!fragment) continue;

      const parsed = parseSectionFragment(fragment);
      if (!parsed || !parsed.columns.length) continue;

      const { meta, columns } = filterMetaFromColumns(parsed.columns);
      if (!columns.length) continue;

      return {
        meta,
        columns,
        footnote: parsed.footnote,
      };
    }

    return null;
  }

  const parseCache = new Map();

  function getParsedDescriptionData(html, headingText) {
    const key = `${html.length}:${normalizeHeading(headingText || 'analiz degerleri')}`;
    if (!parseCache.has(key)) {
      parseCache.set(key, parseDescription(html, headingText));
    }
    return parseCache.get(key);
  }

  function renderRow(row) {
    const classes = ['product-nutrition-panel__row'];
    if (row.strong) classes.push('product-nutrition-panel__row--strong');
    if (row.indent) classes.push('product-nutrition-panel__row--indent');

    const value = row.value != null && row.value !== '' ? row.value : '\u00a0';
    const valueHidden = row.value == null || row.value === '' ? ' aria-hidden="true"' : '';

    return (
      `<div class="${classes.join(' ')}">` +
      `<span>${escapeHtml(row.label)}</span>` +
      `<span class="product-nutrition-panel__row-value"${valueHidden}>${escapeHtml(value)}</span>` +
      `</div>`
    );
  }

  function renderColumn(rows) {
    return `<div class="product-nutrition-panel__table-col">${rows.map(renderRow).join('')}</div>`;
  }

  function renderPanel(root, data) {
    const tablesEl = root.querySelector('[data-hp-nutrition-analiz-tables]');
    const metaEl = root.querySelector('[data-hp-nutrition-analiz-meta]');
    const bodyEl = root.querySelector('[data-hp-nutrition-analiz-body]');
    if (!tablesEl || !bodyEl) return;

    const { meta, columns } = data;
    const hasMeta = meta.calories || meta.dailyHeader;

    if (metaEl) {
      if (hasMeta) {
        metaEl.hidden = false;
        const calRow = metaEl.querySelector('[data-hp-nutrition-calories]');
        const dailyEl = metaEl.querySelector('[data-hp-nutrition-daily-header]');
        if (calRow && meta.calories) {
          calRow.hidden = false;
          const labelSpan = calRow.querySelector('[data-hp-nutrition-calories-label]');
          const valueSpan = calRow.querySelector('[data-hp-nutrition-calories-value]');
          if (labelSpan) labelSpan.textContent = meta.calories.label;
          if (valueSpan) valueSpan.textContent = meta.calories.value || '';
        } else if (calRow) {
          calRow.hidden = true;
        }
        if (dailyEl) {
          dailyEl.textContent = meta.dailyHeader || '% Günlük Değer *';
          dailyEl.hidden = !meta.dailyHeader && !meta.calories;
        }
      } else {
        metaEl.hidden = true;
      }
    }

    const useSingleColumn = columns.length === 1;
    bodyEl.classList.toggle('product-nutrition-panel__table-body--single', useSingleColumn);
    bodyEl.innerHTML = columns.map(renderColumn).join('');

    tablesEl.hidden = false;
    root.hidden = false;
    root.classList.add('is-analiz-loaded');
  }

  function flattenColumns(columns) {
    return columns.reduce((acc, col) => acc.concat(col), []);
  }

  function renderTableImage(root, data) {
    const grid = root.querySelector('[data-hp-nutrition-analiz-grid]');
    if (!grid) return;

    const rows = flattenColumns(data.columns);
    if (!rows.length) return;

    grid.innerHTML = rows
      .map((row, index) => {
        const isLast = index === rows.length - 1;
        const zebra = (index + 1) % 2 === 0 ? ' hp-pdp-table-image__cell--zebra' : '';
        const rowLast = isLast ? ' hp-pdp-table-image__cell--row-last' : '';
        const value =
          row.value != null && row.value !== '' ? escapeHtml(row.value) : '&nbsp;';

        return (
          `<div class="hp-pdp-table-image__cell hp-pdp-table-image__cell--label${zebra}${rowLast}" role="rowheader">${escapeHtml(row.label)}</div>` +
          `<div class="hp-pdp-table-image__cell hp-pdp-table-image__cell--value${zebra}${rowLast}" role="cell">${value}</div>`
        );
      })
      .join('');

    root.hidden = false;
    root.classList.add('is-analiz-loaded');
  }

  function hideTarget(root) {
    const mode = root.getAttribute('data-hp-nutrition-render') || 'panel';
    root.hidden = true;
    root.setAttribute('data-hp-nutrition-empty', 'true');
    if (mode === 'table-image') {
      root.classList.add('hp-pdp-comparison__layout--nutrition-empty');
    }
  }

  function initRoot(root) {
    const headingText = root.getAttribute('data-analiz-heading') || 'Analiz Değerleri';
    const mode = root.getAttribute('data-hp-nutrition-render') || 'panel';
    const descScript =
      root.querySelector(DESC_SELECTOR) || document.querySelector(DESC_SELECTOR);
    if (!descScript) {
      hideTarget(root);
      return;
    }

    let html = '';
    try {
      html = JSON.parse(descScript.textContent || '""');
    } catch {
      html = descScript.textContent || '';
    }

    const data = getParsedDescriptionData(html, headingText);
    if (!data || !data.columns.some((c) => c.length)) {
      hideTarget(root);
      return;
    }

    if (mode === 'table-image') {
      renderTableImage(root, data);
    } else {
      renderPanel(root, data);
    }
  }

  function init() {
    parseCache.clear();
    document.querySelectorAll(PANEL_SELECTOR).forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
